import { useMemo, useState } from "react";

// LOT0F14_ADMIN_PRODUCTION_VALIDATION_CENTER_V1

type ValidationItem = {
  id: string;
  lot: string;
  title: string;
  description: string;
  expected: string;
  risk: "low" | "medium" | "high";
};

type ValidationGroup = {
  id: string;
  title: string;
  goal: string;
  items: ValidationItem[];
};

const GROUPS: ValidationGroup[] = [
  {
    id: "prp",
    title: "PRP terrain",
    goal: "Confirmer que le PRP prospecte, analyse son territoire et alimente le pipeline sans automatisme risqué.",
    items: [
      { id: "tab-return", lot: "0F-7B", title: "Retour d’onglet sans rechargement", description: "Ouvre un prospect, change d’onglet vers Google, puis reviens sur Forfeo.", expected: "Aucun écran Chargement, aucun panneau fermé, scroll conservé.", risk: "high" },
      { id: "nearby-radius-region", lot: "0F-4/0F-13", title: "Nearby 1 à 100 km verrouillé région", description: "Teste un rayon large et vérifie la preuve de région assignée.", expected: "Résultats limités au territoire PRP, position non persistée.", risk: "high" },
      { id: "pipeline-save", lot: "0F-5", title: "Sauvegarde prospect Nearby", description: "Sauvegarde un commerce réel, puis retente le même commerce.", expected: "Premier clic crée le prospect; second clic confirme le doublon contrôlé.", risk: "medium" },
      { id: "route-planner", lot: "0F-6", title: "Tournée terrain intelligente", description: "Sélectionne 4 à 8 prospects et ouvre l’itinéraire.", expected: "Lien de route généré; aucune coordonnée officielle modifiée.", risk: "medium" },
      { id: "demand-radar", lot: "0F-13", title: "Radar de demande / Gap Map", description: "Lance le radar et compare les secteurs sous-couverts au pipeline.", expected: "Analyse visible, signaux non présentés comme parts de marché.", risk: "medium" },
    ],
  },
  {
    id: "partner",
    title: "Candidat partenaire",
    goal: "Valider que le PRP propose, mais que l’admin conserve l’autorité de décision.",
    items: [
      { id: "candidate-submit", lot: "0F-8", title: "Soumission candidat PRP", description: "Depuis le pipeline, soumets un prospect prêt comme candidat.", expected: "Statut Registered; aucune approbation commerce automatique.", risk: "high" },
      { id: "candidate-admin", lot: "0F-9", title: "Validation admin du candidat", description: "Dans Admin → Candidats PRP, approuve ou retourne le dossier.", expected: "Décision admin journalisée; le PRP ne s’auto-approuve pas.", risk: "high" },
      { id: "invite", lot: "0F-10", title: "Invitation onboarding", description: "Crée une invitation sur un candidat approuvé.", expected: "Lien sécurisé 14 jours; token brut jamais stocké.", risk: "high" },
    ],
  },
  {
    id: "merchant",
    title: "Commerce",
    goal: "Fermer le cycle jusqu’au portail commerçant Explore actif, sans déclencher l’écosystème transactionnel.",
    items: [
      { id: "merchant-submit", lot: "0F-10", title: "Soumission commerçant", description: "Le commerçant utilise le lien, se connecte avec le bon email et soumet.", expected: "Demande under_review; attribution PRP conservée.", risk: "high" },
      { id: "merchant-review", lot: "0F-11", title: "Approbation finale commerce", description: "Dans Admin → Onboarding commerces, approuve la demande.", expected: "Portail Explore actif; prospect PRP passe à Active.", risk: "high" },
      { id: "tower", lot: "0F-12", title: "Tour commerciale admin", description: "Ouvre Admin → Tour commerciale.", expected: "KPI, régions, alertes et checklist visibles en lecture seule.", risk: "medium" },
    ],
  },
  {
    id: "safety",
    title: "Garde-fous",
    goal: "Confirmer que les nouveaux parcours demeurent humains, régionaux et non financiers.",
    items: [
      { id: "no-finance", lot: "0F-5→0F-13", title: "Aucune autorité financière", description: "Vérifie les écrans PRP/Admin ajoutés récemment.", expected: "Aucun paiement, remboursement, versement ou transaction boutique.", risk: "high" },
      { id: "no-browser-location-enrichment", lot: "0F-3→0F-13", title: "Aucun géocodage navigateur", description: "Vérifie que Nearby, Radar et Tournée ne géocodent pas côté navigateur.", expected: "Position consentie session-only; recherche fournisseur côté serveur.", risk: "high" },
      { id: "human-only", lot: "0F-5→0F-13", title: "Actions humaines seulement", description: "Teste création prospect, candidat, invitation et approbation.", expected: "Chaque changement important exige un clic humain explicite.", risk: "high" },
    ],
  },
];

const allItems = GROUPS.flatMap((group) => group.items);

const riskClass = (risk: ValidationItem["risk"]) => {
  if (risk === "high") return "bg-rose-100 text-rose-800";
  if (risk === "medium") return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
};

export default function AdminProductionValidationCenterPanel() {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  const total = allItems.length;
  const completed = Object.values(checked).filter(Boolean).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const ready = completed === total;

  const groupProgress = useMemo(() => GROUPS.map((group) => {
    const done = group.items.filter((item) => checked[item.id]).length;
    return { ...group, done, total: group.items.length };
  }), [checked]);

  const report = useMemo(() => {
    const lines = [
      "FORFEO — RAPPORT VALIDATION PRODUCTION",
      `Généré: ${new Date().toLocaleString("fr-CA")}`,
      `Progression: ${completed}/${total} (${percent}%)`,
      "",
    ];

    for (const group of GROUPS) {
      lines.push(`## ${group.title}`);
      for (const item of group.items) {
        lines.push(`${checked[item.id] ? "[x]" : "[ ]"} ${item.lot} — ${item.title}`);
        lines.push(`    Attendu: ${item.expected}`);
      }
      lines.push("");
    }

    lines.push("Bornes: ce centre n’exécute aucun appel serveur, aucun message externe, aucun changement CRM et aucune opération financière.");
    return lines.join("\n");
  }, [checked, completed, percent, total]);

  const toggle = (id: string) => {
    setChecked((previous) => ({ ...previous, [id]: !previous[id] }));
    setCopied(false);
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
    } catch {
      setCopied(false);
      window.prompt("Copie manuelle du rapport :", report);
    }
  };

  return (
    <div className="space-y-6" data-lot0f14-production-validation-center="true">
      <section className="rounded-3xl border border-slate-100 bg-gradient-to-br from-white via-white to-blue-50 p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-700">0F-14 · Validation production</p>
            <h2 className="mt-1 text-2xl font-black text-slate-950">Centre de validation Render / Terrain</h2>
            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
              Checklist opérationnelle pour confirmer le parcours complet PRP → Commerce après déploiement Render. Ce centre ne lit aucune donnée serveur et ne modifie rien : il guide les tests humains et génère un rapport local.
            </p>
          </div>

          <div className="rounded-3xl border border-blue-200 bg-white p-5 text-center shadow-sm">
            <p className="text-4xl font-black text-blue-900">{percent}%</p>
            <p className="mt-1 text-[10px] font-black uppercase tracking-wide text-blue-600">{completed}/{total} validés</p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${ready ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${percent}%` }} />
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          {groupProgress.map((group) => (
            <div className="rounded-2xl border border-slate-100 bg-white p-4" key={group.id}>
              <p className="text-lg font-black text-slate-950">{group.done}/{group.total}</p>
              <p className="mt-1 text-[10px] font-black uppercase tracking-wide text-slate-500">{group.title}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-5">
          {GROUPS.map((group) => (
            <section className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm" key={group.id}>
              <p className="text-lg font-black text-slate-950">{group.title}</p>
              <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">{group.goal}</p>
              <div className="mt-4 space-y-3">
                {group.items.map((item) => (
                  <label className={`block cursor-pointer rounded-2xl border p-4 transition ${checked[item.id] ? "border-emerald-300 bg-emerald-50" : "border-slate-100 bg-slate-50 hover:bg-white"}`} key={item.id}>
                    <div className="flex gap-3">
                      <input checked={Boolean(checked[item.id])} className="mt-1 h-4 w-4" onChange={() => toggle(item.id)} type="checkbox" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-900 px-2 py-1 text-[10px] font-black text-white">{item.lot}</span>
                          <span className={`rounded-full px-2 py-1 text-[10px] font-black uppercase ${riskClass(item.risk)}`}>risque {item.risk}</span>
                        </div>
                        <p className="mt-2 font-black text-slate-950">{item.title}</p>
                        <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">{item.description}</p>
                        <p className="mt-2 rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs font-bold leading-5 text-blue-900">Attendu : {item.expected}</p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </section>
          ))}
        </div>

        <aside className="space-y-4">
          <section className="sticky top-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Rapport</p>
            <h3 className="mt-1 text-xl font-black text-slate-950">Preuve de validation</h3>
            <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">Copie ce rapport dans ton suivi après les tests Render. Les cases cochées restent seulement dans l’état de la page courante.</p>
            <button className="mt-4 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white" onClick={() => void copyReport()} type="button">Copier le rapport</button>
            {copied && <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs font-bold text-emerald-800">Rapport copié.</p>}
            <pre className="mt-4 max-h-[440px] overflow-auto rounded-2xl bg-slate-950 p-4 text-[11px] font-semibold leading-5 text-slate-100">{report}</pre>
          </section>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-bold leading-5 text-amber-900">
            Ce centre de validation ne remplace pas les tests de lot : il confirme le comportement visible après Render. Il n’exécute aucun appel serveur, aucun message externe, aucun changement CRM et aucune opération financière.
          </div>
        </aside>
      </div>
    </div>
  );
}
